import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';
import { FlexBox } from 'components/Containers/FlexBox';
import { ThemePalette } from 'mui.theme';
import type { DeviceStats } from 'types/utilization.types';
import {
  convertMinutesToHours,
  convertsecondsToHours,
  getDataUnit,
} from 'views/Utilization/utils';
import { truncateTo2dp } from 'utils/helpers/general';
import { useMemo } from 'react';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useAppTheme from 'hooks/useAppTheme';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  selectedSource: string[];
  height?: string;
  data: DeviceStats;
  getSourceLabel?: (source: string) => string;
}

const AssetItemChart = ({
  selectedSource,
  height = '230px',
  data,
  getSourceLabel = (source: string) => source,
}: Props) => {
  const { enableDarkTheme } = useAppTheme();
  const dateStats = useMemo(() => data.date_stats, [data]);

  const { unitSystem } = useCustomerInfo();

  const formattedDateLoad: any[] = useMemo(() => {
    const temp = Object.keys(dateStats).map((date) => ({
      date,
      ...dateStats[date],
      idlinghours: Number(
        convertsecondsToHours(Number(dateStats[date].idling_duration))
      ),
      productivehours: Number(
        convertMinutesToHours(
          Number(dateStats[date].utilization_engine_working_minute)
        )
      ),
      avgfuelrate: truncateTo2dp(dateStats[date].hourly_fuel_consumption_l),
      enginehours: Number(
        convertMinutesToHours(
          Number(dateStats[date].utilization_engine_active_minute)
        )
      ),
      utilization: truncateTo2dp(
        Number(dateStats[date].utilization_by_shift_hours) * 100
      ),
      fuel: truncateTo2dp(dateStats[date].fuel_consumption_l),
      production: truncateTo2dp(dateStats[date].max_load_ton),
    }));

    temp.sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());

    return temp;
  }, [JSON.stringify(dateStats)]);

  const getKey = (series: string) => series.toLowerCase().replace(/ /g, ''); // unchanged

  // Compute stats for each series
  const computeStats = (series: string) => {
    const key = getKey(series);
    const dataUnit = getDataUnit(series, unitSystem);

    if (key === 'avgfuelrate') {
      const avgVal = Number(data?.avg_hourly_fuel_consumption_l ?? 0);
      const validPerDay = formattedDateLoad
        .map((entry) => Number(entry.avgfuelrate))
        .filter((v) => v > 0);
      const perDayMax = validPerDay.length ? Math.max(...validPerDay) : 0;
      const maxValue = perDayMax || avgVal;

      return {
        maxValue,
        average: avgVal,
        dataUnit: dataUnit ? `(${dataUnit})` : '',
      };
    }

    if (key === 'utilization') {
      const avgVal = Number(data?.avg_utilization_by_shift_hours ?? 0) * 100;
      const validPerDay = formattedDateLoad
        .map((entry) => Number(entry.utilization))
        .filter((v) => v > 0);

      const perDayMax = validPerDay.length ? Math.max(...validPerDay) : 0;
      const maxValue = perDayMax || avgVal;

      return {
        maxValue,
        average: avgVal,
        dataUnit: dataUnit ? `(${dataUnit})` : '',
      };
    }

    // Default behaviour for other series
    const validValues = formattedDateLoad
      .map((entry) => Number(entry[key]))
      .filter((value) => value > 0);
    const maxValue = validValues.length ? Math.max(...validValues) : 0;
    const average = validValues.length
      ? validValues.reduce((sum, val) => sum + val, 0) / validValues.length
      : 0;

    return { maxValue, average, dataUnit: dataUnit ? `(${dataUnit})` : '' };
  };

  // include data in the deps so memo updates if API summary changes
  const statsDict = useMemo(() => {
    const dict: Record<
      string,
      { average: number; dataUnit: string; maxValue: number }
    > = {};
    selectedSource.forEach((series) => {
      const { average, dataUnit, maxValue } = computeStats(series);
      dict[series] = { average, dataUnit, maxValue };
    });
    return dict;
  }, [
    JSON.stringify(selectedSource),
    JSON.stringify(formattedDateLoad),
    JSON.stringify(data),
  ]);

  let yAxisComponents = null;
  let referenceLines = null;
  let barComponents = null;

  if (selectedSource.length === 1) {
    const { maxValue, average } = computeStats(selectedSource[0]);
    yAxisComponents = (
      <YAxis
        tick={{ fontSize: 10 }}
        domain={[0, Math.ceil(maxValue * 1.1)]}
        label={{
          value: `${getSourceLabel(selectedSource[0])} ${
            statsDict[selectedSource[0]].dataUnit
          }`,
          angle: 270,
          position: 'insideLeft',
          fontSize: 10,
          textAnchor: 'middle',
          dy: 50,
        }}
      />
    );
    referenceLines = (
      <ReferenceLine
        y={average}
        stroke={_palette.dark.primary}
        strokeDasharray="3 3"
        isFront
      />
    );

    barComponents = (
      <Bar
        dataKey={getKey(selectedSource[0])}
        fill={_palette.dark.primary}
        name={getSourceLabel(selectedSource[0])}
      />
    );
  }
  // Two series
  else if (selectedSource.length === 2) {
    const stats1 = computeStats(selectedSource[0]);
    const stats2 = computeStats(selectedSource[1]);
    // Use combined max to unify the domain for both axes
    const combinedMax = Math.max(stats1.maxValue, stats2.maxValue);

    yAxisComponents = (
      <>
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 10 }}
          allowDataOverflow
          domain={[
            0,
            Math.ceil(
              stats1.dataUnit === stats2.dataUnit
                ? combinedMax * 1.1
                : stats1.maxValue * 1.1
            ),
          ]}
          label={{
            value: `${getSourceLabel(selectedSource[0])} ${stats1.dataUnit}`,
            angle: 270,
            position: 'insideLeft',
            fontSize: 10,
            textAnchor: 'middle',
            dy: 50,
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          allowDataOverflow
          tick={{ fontSize: 10 }}
          domain={[
            0,
            Math.ceil(
              stats1.dataUnit === stats2.dataUnit
                ? combinedMax * 1.1
                : stats2.maxValue * 1.1
            ),
          ]}
          label={{
            value: `${getSourceLabel(selectedSource[1])} ${stats2.dataUnit}`,
            angle: 270,
            position: 'insideRight',
            fontSize: 10,
            textAnchor: 'middle',
            dy: -50,
          }}
        />
      </>
    );

    referenceLines = (
      <>
        <ReferenceLine
          yAxisId="left"
          y={stats1.average}
          ifOverflow="extendDomain"
          stroke={_palette.dark.primary}
          strokeDasharray="3 3"
          isFront
        />
        <ReferenceLine
          yAxisId="right"
          y={stats2.average}
          ifOverflow="extendDomain"
          stroke="#FA7B4A"
          strokeDasharray="3 3"
          isFront
        />
      </>
    );

    barComponents = (
      <>
        <Bar
          yAxisId="left"
          dataKey={getKey(selectedSource[0])}
          fill={_palette.dark.primary}
          name={getSourceLabel(selectedSource[0])} // For legend
        />
        <Bar
          yAxisId="right"
          dataKey={getKey(selectedSource[1])}
          fill="#FA7B4A"
          name={getSourceLabel(selectedSource[1])} // For legend
        />
      </>
    );
  }

  return (
    <Box sx={{ paddingTop: '10px' }}>
      <FlexBox
        sx={{
          maxWidth: 'inherit',
          height,
          paddingTop: '20px',
          marginRight: '10px',
          paddingBottom: '10px',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={700}
            height={300}
            data={formattedDateLoad}
            margin={{
              top: 20,
              right: 100,
              left: 5,
              bottom: 5,
            }}
            barSize={20}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={ThemePalette.dark.cartesianGrid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={(timestamp: string) =>
                convertStdDateString(timestamp)
              }
              tick={{ fontSize: 10 }}
            />
            {yAxisComponents}

            {/* Legend */}
            <Legend
              wrapperStyle={{
                fontSize: '11px',
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
              }}
            />

            <Tooltip
              contentStyle={{ backgroundColor: 'transparent', border: 'none' }}
              wrapperStyle={{ outline: 'none', fontSize: '11px' }}
              cursor={{
                fill: enableDarkTheme ? 'rgba(0,0,0,0.15)' : '#CCCCCC',
              }}
              itemStyle={{
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
              }}
              content={
                <CustomTooltip
                  selectedSource={selectedSource}
                  statsDict={statsDict}
                  getSourceLabel={getSourceLabel}
                />
              }
            />

            {barComponents}
            {referenceLines}
          </BarChart>
        </ResponsiveContainer>
      </FlexBox>
    </Box>
  );
};

export default AssetItemChart;

const CustomTooltip = ({
  active,
  payload,
  selectedSource,
  statsDict,
  getSourceLabel = (source: string) => source,
}: any) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();

  if (!active || !payload || !payload.length) {
    return null;
  }

  const hoveredDate = payload[0].payload.date;

  // Example styling for a "box" tooltip
  const boxStyle: React.CSSProperties = {
    // border: enableDarkTheme
    //   ? '1px solid rgba(204, 204, 204, 0.8)'
    //   : '1px solid rgba(27, 43, 74, 0.8)',
    backgroundColor: 'rgba(100, 149, 237, 0.6)',
    borderColor: 'transparent',
    borderRadius: '4px',
    padding: '6px',
    color: enableDarkTheme
      ? ThemePalette.typography.wheat
      : ThemePalette.typography.black,
    fontSize: '10px',
  };

  return (
    <div style={boxStyle}>
      <p>
        {t('fuel_usage_date_label', {
          date: convertStdDateString(hoveredDate),
        })}
      </p>
      {payload.map((entry: any) => {
        const dataKey = entry.dataKey;
        const sourceLabel = selectedSource.find(
          (s: string) => s.toLowerCase().replace(/ /g, '') === dataKey
        );
        if (!sourceLabel) return null;

        const { average, dataUnit } = statsDict[sourceLabel];
        const seriesValue = entry.value;
        const translatedSourceLabel = getSourceLabel(sourceLabel);

        return (
          <div key={dataKey}>
            <p>{`${translatedSourceLabel}: ${seriesValue}${dataUnit || ''}`}</p>
            <p>
              {t('utilization_average_source_value', {
                source: translatedSourceLabel,
                value: `${truncateTo2dp(average)} ${dataUnit || ''}`,
              })}
            </p>
          </div>
        );
      })}
    </div>
  );
};
